import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  model,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';

import { AppIcon } from '../../icons/icon.type';
import { AppIconsLibrary } from '../../icons/icons-library.service';
import { AccordionService } from '../accordion.service';
import { ButtonTapDirective } from 'core/directives/button-tap.directive';

@Component({
  selector: 'app-accordion-item',
  templateUrl: './accordion-item.component.html',
  styleUrl: './accordion-item.component.scss',
  host: { ngSkipHydration: 'true' },
  animations: [
    trigger('collapse', [
      transition(':enter', [style({ height: 0 }), animate('{{durationMs}}ms ease', style({ height: '*' }))]),
      transition(':leave', [style({ height: '*' }), animate('{{durationMs}}ms ease', style({ height: 0 }))]),
    ]),
  ],
})
export class AccordionItemComponent implements OnInit, OnDestroy {
  @Input()
  @HostBinding('class.disabled')
  disabled?: boolean;

  @Input()
  showArrow = true;

  @Input()
  expectedMaxHeight = 1000;

  @Input()
  highlightInsetY = 6;

  @Input()
  highlightInsetX = 0;

  @Input()
  title = '';

  @Input()
  content = '';

  @Input()
  icon?: AppIcon;

  @Input()
  highlightStyle: ButtonTapDirective['highlightStyle'] = 'light';

  @HostBinding('class.active')
  @Input()
  showBody = false;

  @Input({ required: true })
  index: number = 0;

  $durationMs = model(0);

  private _icons = inject(AppIconsLibrary);
  private _destroyRef = inject(DestroyRef);

  constructor(
    protected elementRef: ElementRef<HTMLElement>,
    @Optional() private accordionService?: AccordionService,
  ) {
    this.icon = this._icons.getIcon('angle-down');
    timer(400)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.$durationMs.set(350);
      });
  }

  ngOnInit() {
    this.accordionService?.activeIndices$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(activeIndices => {
      this.showBody = activeIndices.includes(this.index);
    });
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.remove();
  }

  toggle() {
    if (this.disabled) {
      return;
    }
    if (this.accordionService) {
      this.accordionService.toggle(this.index);
    } else {
      this.showBody = !this.showBody;
    }
  }

  projectContentChanged(el: HTMLDivElement) {
    el.style.maxHeight = `${el.clientHeight}px`;
    timer(this.$durationMs())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        el.style.maxHeight = '';
      });
  }
}
